/** @format */

import axios from "axios";
import { Input } from "antd";
import { NavLink } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { SearchOutlined } from "@ant-design/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";

import pdf from "../../../../icons/pdf.png";
import print from "../../../../icons/print.png";
import excel from "../../../../icons/excel.png";
import { header } from "../../../../components/api/Header";
import LoadingCard from "../../../../components/loading/loadingCard";
import { ExcelPrint, Pdf } from "../../../../components/functions/Print";
import { getEnv } from "../../../../lib/env";
import { formatReservoirName } from "./helpers";

const TABLE_TITLE =
  "Respublika va respublikalararo suv omborlaridagi mavjud suv resurslari tahlili";
const VALUE_KEYS = [
  "hajmi",
  "hajmi1yil",
  "hajmi2yil",
  "hajmi3yil",
  "ortacha_kop_yillik",
  "ortacha_farqi",
  "ortacha_foiz",
  "otgan_yil_farqi",
  "otgan_yil_foiz",
  "kelishi",
  "kelishi1yil",
  "kelishi_farqi",
  "chiqishi",
  "chiqishi1yil",
  "chiqishi_farqi",
];

const formatHeaderDate = (value) => {
  if (!value) return "";
  return value.split("-").reverse().join(".");
};

const HIDDEN_DETAIL_ROW_IDS = new Set([28, 29, 30]);

const normalizeReservoirName = (value) =>
  formatReservoirName(value)
    .toLowerCase()
    .replace(/[`'’‘ʻʼ]/g, "")
    .replace(/[^a-z0-9а-яёҳқғў]+/gi, "");

const shouldHideRow = (row) => {
  const name = normalizeReservoirName(row?.nomi);

  if (row.type === "group") {
    return name === "shujumladan";
  }

  return (
    row.type === "data" &&
    (HIDDEN_DETAIL_ROW_IDS.has(Number(row.id)) ||
      name.includes("ozan") ||
      name.includes("kaparas") ||
      name.includes("sultonsanjar"))
  );
};

function Table4({ date, setOmbor, setLoading, loading }) {
  const conponentPDF = useRef();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const selectedDateLabel = formatHeaderDate(date);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${getEnv("REACT_APP_BASE_URL")}/v1/republik/suvombor/so-tahlil-viloyatlar?date=${date}`,
          header()
        );
        setData(res.data || []);
      } catch (e) {
        console.log("Xato", e);
        alert("Bazaga bog'lanishda xato");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [date, setLoading]);

  const visibleRows = useMemo(() => {
    const text = searchText.trim().toUpperCase();
    const filteredData = data.filter((row) => !shouldHideRow(row));

    if (!text) {
      return filteredData;
    }

    return filteredData.filter((row) => {
      if (row.type !== "data") {
        return false;
      }

      return String(row.nomi || "").toUpperCase().includes(text);
    });
  }, [data, searchText]);
  const displayRows = useMemo(() => {
    const createAvailability = () =>
      VALUE_KEYS.reduce((result, key) => {
        result[key] = false;
        return result;
      }, {});
    const rows = [];
    let groupAvailability = createAvailability();
    let allAvailability = createAvailability();

    visibleRows.forEach((row) => {
      if (row.type === "group") {
        if (String(row.nomi || "").toLowerCase() !== "shu jumladan") {
          groupAvailability = createAvailability();
        }

        rows.push(row);
        return;
      }

      if (row.type === "data") {
        VALUE_KEYS.forEach((key) => {
          if (hasNumericValue(row[key])) {
            groupAvailability[key] = true;
            allAvailability[key] = true;
          }
        });

        rows.push(row);
        return;
      }

      if (row.type === "total") {
        const isAllTotal = String(row.nomi || "").toLowerCase() === "hammasi";

        rows.push({
          ...row,
          __valueAvailability: isAllTotal ? allAvailability : groupAvailability,
        });
        return;
      }

      rows.push(row);
    });

    return rows;
  }, [visibleRows]);

  const generatePDF = useReactToPrint({
    content: () => conponentPDF.current,
    documentTitle: TABLE_TITLE,
    onAfterPrint: () => alert("Data saved in PDF"),
  });

  let rowNumber = 0;

  return (
    <>
      <div className='table-content-top'>
        <div className='table-top-left'>
          <Input
            prefix={<SearchOutlined className='site-form-item-icon' />}
            placeholder='Search'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <ul className='table-top-right'>
          <li>
            <button className='btn p-0' onClick={() => generatePDF()}>
              <img src={print} alt='404' />
            </button>
          </li>

          <li>
            <button onClick={() => Pdf("so4", true, date)} className='btn p-0'>
              <img src={pdf} alt='404' />
            </button>
          </li>

          <li>
            <button
              className='btn p-0'
              onClick={() =>
                ExcelPrint(
                  "SO tahlili Viloyatlar kesimida",
                  "Sheet 1",
                  "so4",
                  date
                )
              }>
              <img src={excel} alt='404' />
            </button>
          </li>
        </ul>
      </div>

      {loading ? (
        <LoadingCard loading={loading} heightSize='80vh' />
      ) : (
        <div className='table-responsive tableFixHead' id='tableFlexible'>
          <div ref={conponentPDF} style={{ width: "100%" }}>
            <table
              className='table table-bordered table-style suv-reservoir-table'
              id='so4'>
              <thead>
                <tr>
                  <th colSpan={17} scope='col'>
                    {TABLE_TITLE}
                  </th>
                </tr>

                <tr>
                  <th rowSpan={3} scope='col'>
                    N
                  </th>
                  <th rowSpan={3} scope='col'>
                    Suv omborlar nomi
                  </th>
                  <th colSpan={9} scope='col'>
                    Hajmi (mln.m<sup>3</sup>)
                  </th>
                  <th colSpan={3} scope='col'>
                    Kelishi, m<sup>3</sup>/sek
                  </th>
                  <th colSpan={3} scope='col'>
                    Chiqishi, m<sup>3</sup>/sek
                  </th>
                </tr>

                <tr>
                  <th rowSpan={2} scope='col'>
                    {selectedDateLabel}
                  </th>
                  <th colSpan={3} scope='col'>
                    Oldingi yillar shu kunga
                  </th>
                  <th rowSpan={2} scope='col'>
                    O'rtacha ko'p yillik
                  </th>
                  <th colSpan={2} scope='col'>
                    O'rtachaga nisbatan
                  </th>
                  <th colSpan={2} scope='col'>
                    O'tgan yilga nisbatan
                  </th>
                  <th rowSpan={2} scope='col'>
                    {date.slice(0, 4)}
                  </th>
                  <th rowSpan={2} scope='col'>
                    {date.slice(0, 4) - 1}
                  </th>
                  <th rowSpan={2} scope='col'>
                    Farqi +-
                  </th>
                  <th rowSpan={2} scope='col'>
                    {date.slice(0, 4)}
                  </th>
                  <th rowSpan={2} scope='col'>
                    {date.slice(0, 4) - 1}
                  </th>
                  <th rowSpan={2} scope='col'>
                    Farqi +-
                  </th>
                </tr>

                <tr>
                  <th scope='col'>{date.slice(0, 4) - 1}</th>
                  <th scope='col'>{date.slice(0, 4) - 2}</th>
                  <th scope='col'>{date.slice(0, 4) - 3}</th>
                  <th scope='col'>Farqi +-</th>
                  <th scope='col'>%</th>
                  <th scope='col'>Farqi +-</th>
                  <th scope='col'>%</th>
                </tr>
              </thead>

              <tbody>
                {displayRows.map((row, index) => {
                  if (row.type === "group") {
                    if (String(row.nomi || "").toLowerCase() === "shu jumladan") {
                      return (
                        <tr className='j-tr' key={index}>
                          <td></td>
                          <td>{row.nomi}</td>
                          {Array.from({ length: 15 }).map((_, cellIndex) => (
                            <td key={cellIndex}></td>
                          ))}
                        </tr>
                      );
                    }

                    return (
                      <tr className='table-one-row j-tr' key={index}>
                        <td colSpan={17}>{row.nomi}</td>
                      </tr>
                    );
                  }

                  if (row.type === "total") {
                    return (
                      <tr className='j-tr' key={index}>
                        <td colSpan={2}>{row.nomi}</td>
                        {renderValues(row, row.__valueAvailability)}
                      </tr>
                    );
                  }

                  rowNumber += 1;

                  return (
                    <tr className='data-tr' key={index}>
                      <td>{rowNumber}</td>
                      <td className='text-start fix-with'>
                        <NavLink
                          style={{ color: "#222" }}
                          to={"/suvomborlari/" + row.id}
                          onClick={() =>
                            setOmbor({
                              nomi: row.nomi,
                              location: [row.lat, row.lan],
                            })
                          }>
                          {formatReservoirName(row.nomi)}
                        </NavLink>
                      </td>
                      {renderValues(row)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default Table4;

const renderValues = (row, availability) => (
  <>
    {VALUE_KEYS.map((key) => (
      <td key={key}>{filteredValue(row[key], availability?.[key])}</td>
    ))}
  </>
);

const filteredValue = (value, hasValue) => {
  if (hasValue === false) {
    return "-";
  }

  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = parseFloat(value);
  if (Number.isNaN(number)) {
    return "-";
  }

  const rounded = Math.round(number * 100) / 100;

  return rounded % 1 === 0 ? Math.floor(rounded) : rounded;
};

const hasNumericValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  return !Number.isNaN(parseFloat(value));
};
